import ApiService from '@/core/services/api.service';
import {
  validateFileSize,
  formatFileSizeMB,
  isImageFile,
  isPdfFile,
  getApiUrlByEntity,
} from '@/utils/common-upload-files';

const apiUrl = getApiUrlByEntity('test-drives');

// Cập nhật để hỗ trợ thêm file types cho test drive
function checkTestDriveFileType(fileName) {
  const validExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
  if (fileName.length > 0) {
    for (const extension of validExtensions) {
      const currentExtension = fileName.substr(
        fileName.length - extension.length,
        extension.length,
      );
      if (currentExtension.toLowerCase() === extension.toLowerCase()) {
        return true;
      }
    }
  }
  return false;
}

const TestDriveFilesService = {
  /**
   * Validate files trước khi upload
   * @param {FileList} files - Files to validate
   * @returns {Object} - Validation result
   */
  validateFiles(files) {
    const errors = [];
    const validFiles = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check file type
      if (!checkTestDriveFileType(file.name)) {
        errors.push(
          `File "${file.name}" không đúng định dạng. Chỉ hỗ trợ JPG, PNG, PDF`,
        );
        continue;
      }

      // Check file size (tối đa 10MB)
      if (!validateFileSize(file, 10)) {
        errors.push(
          `File "${file.name}" có kích thước vượt quá ${formatFileSizeMB(
            10 * 1024 * 1024,
          )}`,
        );
        continue;
      }

      validFiles.push(file);
    }

    return {
      isValid: errors.length === 0,
      errors,
      validFiles,
    };
  },

  /**
   * Upload files for test drive
   * @param {string} testDriveId - Test drive ID
   * @param {Array} files - Array of files
   * @returns {Promise}
   */
  async uploadFiles(testDriveId, files) {
    const formData = new FormData();
    formData.append('entityId', testDriveId);

    // Add multiple files
    files.forEach((file) => {
      formData.append('files', file);
    });

    return ApiService.post(apiUrl.upload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Get list of files for test drive
   * @param {string} testDriveId - Test drive ID
   * @returns {Promise}
   */
  async getFiles(testDriveId) {
    return ApiService.query(apiUrl.root, {
      params: {
        entityId: testDriveId,
      },
    });
  },

  /**
   * Preview file
   * @param {string} testDriveId - Test drive ID
   * @param {string} fileName - File name
   * @returns {Promise}
   */
  async previewFile(testDriveId, fileName) {
    return ApiService.query(apiUrl.preview, {
      params: {
        entityId: testDriveId,
        fileName: fileName,
      },
      responseType: 'blob',
    });
  },

  /**
   * Download file
   * @param {number} fileId - File ID
   * @returns {Promise}
   */
  async downloadFile(fileId) {
    return ApiService.query(`${apiUrl.root}/download/${fileId}`, {
      responseType: 'blob',
    });
  },

  /**
   * Delete file
   * @param {number} fileId - File ID
   * @returns {Promise}
   */
  async deleteFile(fileId) {
    return ApiService.delete(`${apiUrl.root}/${fileId}`);
  },

  /**
   * Create preview URL for file
   * @param {File} file - File object
   * @returns {string|null} - Preview URL or null
   */
  createPreviewUrl(file) {
    if (isImageFile(file.type) || isPdfFile(file.type)) {
      return URL.createObjectURL(file);
    }
    return null;
  },

  /**
   * Get file type for preview
   * @param {File|string} file - File object or file name
   * @returns {string} - File type ('image', 'pdf', 'other')
   */
  getFileType(file) {
    const fileType = typeof file === 'string' ? file : file.type;
    const fileName = typeof file === 'string' ? file : file.name;

    if (isImageFile(fileType)) {
      return 'image';
    }

    if (isPdfFile(fileType)) {
      return 'pdf';
    }

    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) {
        return 'image';
      }
      if (extension === 'pdf') {
        return 'pdf';
      }
    }

    return 'other';
  },
};

export { TestDriveFilesService };
